import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { GET_QUOTATION } from '../../apollo/queries/quotationQueries';
import '../css/QuotationDetailPage.css';
import ImageWithPlaceholder from '../../components/ImageWithPlaceholder';

const QuotationDetailPage = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const quotationId = searchParams.get('id');

    // GraphQL 쿼리로 실제 견적 데이터 가져오기
    const { data, loading, error } = useQuery(GET_QUOTATION, {
        variables: { id: parseInt(quotationId) },
        skip: !quotationId,
        errorPolicy: 'all',
        fetchPolicy: 'cache-and-network'
    });

    // PDF 출력 기능
    const handlePrintPDF = () => {
        window.print();
    };

    // 견적 요청하기 (새로운 견적 요청)
    const handleRequestQuote = () => {
        navigate('/cart');
    };

    // 파일 다운로드 기능
    const handleFileDownload = (url, fileName) => {
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName || 'download';
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // 파일 타입 확인 함수들
    const isImageFile = (fileName) => {
        const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.tiff', '.tif', '.webp'];
        return imageExtensions.some(ext => fileName.toLowerCase().endsWith(ext));
    };

    const isPdfFile = (fileName) => {
        return fileName.toLowerCase().endsWith('.pdf');
    };

    // 관리자 답변에서 파일 정보 추출
    const parseAdminReplyFiles = (adminReply) => {
        if (!adminReply) return { text: '', files: [] };
        
        // 파일 패턴 매칭: [첨부파일: 파일명] 및 URL 추출
        const filePattern = /\[첨부파일:\s*([^\]]+)\]/g;
        const urlPattern = /https?:\/\/[^\s]+/g;
        
        const files = [];
        const fileMatches = [...adminReply.matchAll(filePattern)];
        const urlMatches = [...adminReply.matchAll(urlPattern)];
        
        // 파일명과 URL 매칭
        fileMatches.forEach((match, index) => {
            const fileName = match[1].trim();
            const url = urlMatches[index] ? urlMatches[index][0] : null;
            if (url) {
                files.push({ fileName, url });
            }
        });
        
        // 파일 정보 제거한 텍스트
        let cleanText = adminReply
            .replace(filePattern, '')
            .replace(urlPattern, '')
            .replace(/파일 다운로드:\s*/g, '')
            .trim();
        
        return { text: cleanText, files };
    };

    // 이미지 파싱 함수
    const parseProductImage = (images) => {
        if (!images) return null;
        
        try {
            if (typeof images === 'string') {
                const cleanedImages = images.trim();
                if (cleanedImages && cleanedImages !== '[]' && cleanedImages !== '') {
                    if (cleanedImages.startsWith('[')) {
                        const parsedImages = JSON.parse(cleanedImages);
                        if (Array.isArray(parsedImages) && parsedImages.length > 0) {
                            return parsedImages[0];
                        }
                    } else if (cleanedImages.includes('http') || cleanedImages.includes('/')) {
                        return cleanedImages;
                    }
                }
            } else if (Array.isArray(images) && images.length > 0) {
                return images[0];
            }
        } catch (e) {
            // JSON 파싱 실패 시 무시
        }
        return null;
    };

    // 로딩 상태
    if (loading) {
        return (
            <div className="quotation-detail-page">
                <div style={{ textAlign: 'center', padding: '50px' }}>
                    <div>견적 상세 정보를 불러오는 중입니다...</div>
                </div>
            </div>
        );
    }

    // 에러 상태
    if (error) {
        return (
            <div className="quotation-detail-page">
                <div style={{ textAlign: 'center', padding: '50px', color: 'red' }}>
                    <div>견적 상세 정보를 불러오는 중 오류가 발생했습니다: {error.message}</div>
                </div>
            </div>
        );
    }

    const quotation = data?.quotation;

    if (!quotation) {
        return (
            <div className="quotation-detail-page">
                <div style={{ textAlign: 'center', padding: '50px' }}>
                    <div>견적을 찾을 수 없습니다.</div>
                </div>
            </div>
        );
    }

    // 견적 세부 정보 구성
    const quotationDetails = {
        number: quotation.id,
        date: new Date(quotation.createdAt).toLocaleDateString('ko-KR'),
        manager: quotation.user?.name ? `${quotation.user.name} (${quotation.user.email})` : '정보 없음',
        estimatedRelease: quotation.updatedAt ? new Date(quotation.updatedAt).toLocaleDateString('ko-KR') : 'PDF 기재',
        emailSentTo: quotation.user?.email ? `(${quotation.user.email})으로 발송` : '이메일 정보 없음',
        status: quotation.status === 'PENDING' ? '견적 대기' : 
                quotation.status === 'COMPLETED' ? '견적 완료' : 
                quotation.status === 'CANCELLED' ? '취소됨' : quotation.status
    };

    return (
        <div className="quotation-detail-page">
            <div className="qd-breadcrumb">
                <span>홈</span> &gt; <span>내 정보</span> &gt; <span>견적 이력</span> &gt; <span className="current">견적 상세</span>
            </div>
            <h1 className="qd-main-title">견적 상세</h1>

            <div className="qd-summary-box">
                <div className="qd-summary-vertical">
                    <div className="qd-summary-item"><span className="label">견적 번호</span><span>{quotationDetails.number}</span></div>
                    <div className="qd-summary-item"><span className="label">견적 일자</span><span>{quotationDetails.date}</span></div>
                    <div className="qd-summary-item"><span className="label">견적 담당자</span><span>{quotationDetails.manager}</span></div>
                    <div className="qd-summary-item"><span className="label">예상 출고일</span><span>{quotationDetails.estimatedRelease}</span></div>
                    <div className="qd-summary-item"><span className="label">이메일 발송</span><span>{quotationDetails.emailSentTo}</span></div>
                    <div className="qd-summary-item"><span className="label">상태</span><span>{quotationDetails.status}</span></div>
                </div>
                <div className="qd-summary-actions">
                    <button className="qd-btn-dark" onClick={handleRequestQuote}>견적 요청하기</button>
                    <button className="qd-btn-light" onClick={handlePrintPDF}>PDF 출력하기</button>
                </div>
            </div>
            
            {/* 자체 제작 견적과 일반 상품 견적을 구분하여 표시 */}
            {quotation.quotationItems && quotation.quotationItems.length > 0 ? (
                // 일반 상품 견적의 경우
                <>
            <div className="qd-product-list-header">
                        <div className="qd-header-cell">제품 명</div>
                        <div className="qd-header-cell">수량</div>
                        <div className="qd-header-cell">비고 (탭 or 기장)</div>
                        <div className="qd-header-cell">희망 납기일</div>
            </div>

            <div className="qd-product-list">
                        {quotation.quotationItems.map(item => {
                            const imageUrl = parseProductImage(item.product.images);

                            return (
                                <div key={item.id} className="qd-product-item">
                                    <div className="qd-product-info">
                                        <ImageWithPlaceholder 
                                            src={imageUrl} 
                                            alt={item.product.name}
                                            className="qd-product-image"
                                        />
                                        <div className="qd-product-details">
                                            <h4 className="qd-product-name">{item.product.name}</h4>
                                            <p className="qd-product-code">코드: {item.product.id}</p>
                                            <p className="qd-product-description">{item.product.description || '상세 정보 없음'}</p>
                                            {item.product.mainCategory && (
                                                <p className="qd-product-category">
                                                    카테고리: {item.product.mainCategory.name}
                                                    {item.product.subCategory && ` > ${item.product.subCategory.name}`}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                    
                                    <div className="qd-product-details-grid">
                                        <div className="qd-detail-item">
                                            <div className="qd-detail-label">수량</div>
                                            <div className="qd-detail-value">{item.quantity}개</div>
                                        </div>
                                        <div className="qd-detail-item">
                                            <div className="qd-detail-label">비고 (탭 or 기장)</div>
                                            <div className="qd-detail-value">{item.product.specifications || '없음'}</div>
                                        </div>
                                        <div className="qd-detail-item">
                                            <div className="qd-detail-label">희망 납기일</div>
                                            <div className="qd-detail-value">
                                                {quotation.updatedAt ? new Date(quotation.updatedAt).toLocaleDateString('ko-KR') : '미정'}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </>
            ) : (
                // 자체 제작 견적의 경우
                <div className="qd-custom-order-section">
                    <div className="qd-custom-order-header">
                        <h3>주문 제작 견적 요청</h3>
                        <p className="qd-custom-order-description">
                            고객 맞춤형 제작 견적입니다. 아래 상세 내용과 첨부 파일을 확인해주세요.
                        </p>
                    </div>
                    
                    <div className="qd-custom-order-info">
                        <div className="qd-info-item">
                            <span className="qd-info-label">견적 유형</span>
                            <span className="qd-info-value">주문 제작</span>
                        </div>
                        <div className="qd-info-item">
                            <span className="qd-info-label">희망 납기일</span>
                            <span className="qd-info-value">
                                {quotation.updatedAt ? new Date(quotation.updatedAt).toLocaleDateString('ko-KR') : '협의'}
                            </span>
                        </div>
                        <div className="qd-info-item">
                            <span className="qd-info-label">처리 상태</span>
                            <span className="qd-info-value">{quotationDetails.status}</span>
                        </div>
                    </div>
            </div>
            )}

            {quotation.message && (
            <div className="qd-inquiry-box">
                <span className="label">문의 사항</span>
                <div className="content">
                        <p>{quotation.message}</p>
                    </div>
                </div>
            )}

            {quotation.adminReply && (
                <div className="qd-reply-box">
                    <span className="label">관리자 답변</span>
                    <div className="content">
                        {(() => {
                            const { text, files } = parseAdminReplyFiles(quotation.adminReply);
                            return (
                                <>
                                    {text && <p style={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}>{text}</p>}
                                    {files.length > 0 && (
                                        <div className="attached-files">
                                            <h4>첨부 파일</h4>
                                            {files.map((file, index) => (
                                                <div key={index} className="file-item">
                                                    <span className="file-name">📎 {file.fileName}</span>
                                                    <div className="file-actions">
                                                        <button 
                                                            className="btn-download"
                                                            onClick={() => handleFileDownload(file.url, file.fileName)}
                                                        >
                                                            다운로드
                                                        </button>
                                                        <button 
                                                            className="btn-preview"
                                                            onClick={() => window.open(file.url, '_blank')}
                                                        >
                                                            미리보기
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </>
                            );
                        })()}
                    </div>
                </div>
            )}

            {/* 고객 첨부파일 섹션 */}
            {quotation.customerFiles && quotation.customerFiles.length > 0 && (
                <div className="qd-attachment-box">
                    <span className="label">내가 첨부한 파일</span>
                    <div className="content">
                        {quotation.customerFiles.map((file, index) => (
                            <div key={index} className="attachment-file-item">
                                <span className="file-name">📎 {file.name}</span>
                                <span className="file-size">({(file.size / 1024).toFixed(1)}KB)</span>
                                <div className="file-actions">
                                    <button 
                                        className="btn-download"
                                        onClick={() => handleFileDownload(file.url, file.name)}
                                    >
                                        다운로드
                                    </button>
                                    {(isImageFile(file.name) || isPdfFile(file.name)) && (
                                        <button 
                                            className="btn-preview"
                                            onClick={() => window.open(file.url, '_blank')}
                                        >
                                            미리보기
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* 관리자 첨부파일이 별도로 있는 경우 표시 */}
            {quotation.attachmentUrl && (
                <div className="qd-attachment-box">
                    <span className="label">관리자 첨부파일</span>
                    <div className="content">
                        <div className="attachment-file-item">
                            <span className="file-name">📎 견적서 파일</span>
                            <div className="file-actions">
                                <button 
                                    className="btn-download"
                                    onClick={() => handleFileDownload(quotation.attachmentUrl, '견적서.pdf')}
                                >
                                    다운로드
                                </button>
                                <button 
                                    className="btn-preview"
                                    onClick={() => window.open(quotation.attachmentUrl, '_blank')}
                                >
                                    미리보기
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="qd-page-actions">
                <button className="qd-btn-dark" onClick={() => navigate('/quotation-history')}>돌아가기</button>
                <button className="qd-btn-primary" onClick={() => navigate('/order-guide')}>발주 안내</button>
            </div>
        </div>
    );
};

export default QuotationDetailPage; 